<template lang="pug">
  .v-textarea(:class="classes")
    textarea.v-textarea__field(
      required
      v-focus="focus"
      :id="fieldId"
      :name="name || fieldId"
      :value="value"
      :disabled="disabled"
      :rows="rows"
      :placeholder="placeholder"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      ref="textarea")
    label(:for="fieldId") {{ label }}
    .v-textarea__bar
    .v-textarea__hint(v-if="hint") {{ hint }}
</template>

<script>
export default {
  name: 'VTextarea',

  props: {
    label: String,
    value: [String, Number],
    disabled: Boolean,
    name: String,
    id: String,
    rows: {
      type: [String, Number],
      default: 4
    },
    placeholder: {
      type: String,
      default: ''
    },
    invalid: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: null
    },
    focus: {
      type: Boolean,
      default: false
    },
    autoResize: {
      type: Boolean,
      default: true
    },
    maxLength: {
      type: Number,
      default: null
    }
  },

  computed: {
    classes () {
      return {
        'v-textarea--invalid': this.invalid,
        'v-textarea--focused': this.isFocused,
        'v-textarea--has-value': this.hasValue
      }
    },

    hasValue () {
      return this.value !== null && this.value !== undefined && this.value.toString().trim().length > 0
    },

    fieldId () {
      return this.id || `textarea-${this._uid}`
    }
  },

  data () {
    return {
      isFocused: false
    }
  },

  mounted () {
    if (this.autoResize) {
      this.adjustHeight()
    }
  },

  methods: {
    onInput (e) {
      let value = e.target.value

      if (this.maxLength && value.length > this.maxLength) {
        value = value.slice(0, this.maxLength)
        e.target.value = value
      }

      if (this.autoResize) {
        this.adjustHeight()
      }

      this.$emit('input', value)
    },

    onFocus (e) {
      this.isFocused = true
      this.$emit('focus', e)
    },

    onBlur (e) {
      this.isFocused = false
      const value = e.target.value

      let isValid = true

      if (this.maxLength && value.length > this.maxLength) {
        isValid = false
      }

      this.isValidOnBlur = isValid && value.trim() !== ''
      this.$emit('blur', e)
    },

    adjustHeight () {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea
        if (textarea) {
          textarea.style.height = 'auto'

          const scrollHeight = textarea.scrollHeight
          const minHeight = parseInt(this.rows) * 24
          const maxHeight = minHeight * 3

          const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
          textarea.style.height = newHeight + 'px'
        }
      })
    }
  },

  watch: {
    value () {
      if (this.autoResize) {
        this.adjustHeight()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-textarea {
  position: relative;
  margin-bottom: 1rem;

  &__field {
    width: 100%;
    font-size: 1.5rem;
    padding: 1rem 1.5rem;
    min-height: 5rem;
    display: block;
    font-weight: 900;
    border: none;
    border-radius: 1rem;
    background: #EEEEEE;
    resize: none;
    font-family: inherit;
    line-height: 1.5;
    transition: background-color 0.2s ease;

    &:focus {
      outline: none;
    }

    &:focus ~ label,
    &:valid ~ label {
      top: -2rem;
      font-size: 1.3rem;
      color: $color-gray;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      
      ~ label {
        color: $color-gray-light;
      }
    }

    &:focus ~ .v-textarea__bar:before,
    &:focus ~ .v-textarea__bar:after {
      width: 50%;
    }
  }

  &__bar {
    position: relative;

    &:before,
    &:after {
      content: "";
      width: 0;
      height: .2rem; 
      bottom: .1rem; 
      position: absolute;
      transition: 0.2s ease all;
    }

    &:before {
      left: 50%;
    }

    &:after {
      right: 50%; 
    }
  }
  
  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
    padding-top: .3rem;
    min-height: 1.8rem;
  }

  label {
    top: 2rem;
    transform: translateY(-50%);
    left: 1rem;
    color: $color-black; 
    font-size: 1.5rem;
    position: absolute;
    pointer-events: none;
    transition: 0.2s ease all;
    background: transparent;
  }

  &--focused,
  &--has-value {
    label {
      top: -2rem;
      transform: translateY(0);
      font-size: 1.3rem;
      color: $color-gray;
    }
  }

  &--has-value {
    .v-textarea__field {
      background-color: #F4F4F4;
    }
  }

  &--focused {
    .v-textarea__field {
      background-color: #F4F4F4;
    }
  }

  &--valid-blur {
    .v-textarea__field {
      background-color: #DDF2E3;
    }
  }

  &--invalid {
    label {
      color: $color-red !important;
    }

    .v-textarea__field {
      color: $color-red !important;
      border-bottom: .2rem solid $color-red !important;
    }

    .v-textarea__bar {
      &:before,
      &:after {
        background-color: transparent !important;
      }
    }

    .v-textarea__hint {
      color: $color-red;
    }
  }
}

// Адаптивність
@media (max-width: 768px) {
  .v-textarea {
    &__field {
      font-size: 1.4rem;
      padding: 0.8rem 1.2rem;
    }

    label {
      font-size: 1.4rem;
      left: 1.2rem;
    }

    &__hint {
      font-size: 1.1rem;
    }

    &--focused,
    &--has-value {
      label {
        top: -1.3rem;
      }
    }
  }
}
</style>